/**
 * NIfTI Manager — Enterprise Volume Loader & Exporter
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6
 *
 * [AUDIT] v6.0 — MEMORY SAFE & ASYNC EDITION
 * 
 * ИСПРАВЛЕНИЯ И АРХИТЕКТУРА:
 * 1. [CRITICAL] Устранена утечка памяти: URL.revokeObjectURL теперь вызывается всегда после загрузки тома в RAM.
 * 2. [CRITICAL] Убран хардкодный setTimeout(1000). Внедрено интеллектуальное ожидание завершения загрузки тома.
 * 3. [FIX] Promise-блокировка инициализации: предотвращает гонку (Race Condition) при множественных вызовах init.
 * 4. [FIX] Структурирован экспорт NIfTI-1: убраны "магические числа", добавлена строгая типизация хедера.
 * 5. [PERF] Полная инкапсуляция через ES2022 класс + Телеметрия операций.
 */

import { cornerstone } from '../core/init.js';
import { log } from '../utils/logger.js';
import { buildNiftiBytes } from './niftiCore.js';   // [11.1.70] один хозяин записи байтов
// [9.0.0] NIfTI-том тоже создаётся мимо loadVolume — заносим в реестр,
// иначе после ухода вкладки в фон восстанавливать его будет нечем.
import { rememberVolume } from '../core/volumeRegistry.js';

// [v8 FIX] Fallback: если cornerstone не найден через named export
const _cs = cornerstone || globalThis.cornerstone || null;
function _getCS() {
  if (_cs) return _cs;
  // Попытка динамического импорта уже закешированного модуля
  try { return require('@cornerstonejs/core'); } catch { /* noop */ }
  throw new NiftiModuleError('Cornerstone3D core not available. Ensure init.js exports { cornerstone }.');
}

/**
 * Кастомный класс ошибок для модуля NIfTI
 */
export class NiftiModuleError extends Error {
  constructor(message, details = {}) {
    super(message);
    this.name = 'NiftiModuleError';
    this.details = details;
  }
}

/**
 * Enterprise NIfTI Manager
 */
class NiftiManager {
  #isRegistered = false;
  #initPromise = null;

  // ==========================================
  // 🛡 TELEMETRY DECORATOR
  // ==========================================
  #withTelemetry(actionName, fn) {
    return async (...args) => {
      const start = performance.now();
      try {
        const result = await fn.apply(this, args);
        const duration = (performance.now() - start).toFixed(2);
        log.debug(`[Metrics] ⏱️ NiftiManager.${actionName} took ${duration}ms`);
        return result;
      } catch (error) {
        log.error(`[Metrics] ❌ NiftiManager.${actionName} FAILED:`, error.message);
        throw new NiftiModuleError(`Failed executing ${actionName}`, { originalError: error });
      }
    };
  }

  // ==========================================
  // 🏗 ИНИЦИАЛИЗАЦИЯ И ЗАГРУЗКА
  // ==========================================

  initLoader = this.#withTelemetry('initLoader', async () => {
    if (this.#isRegistered) return;
    
    // Блокировка от состояния "гонки" (Race Condition), если вызвали init дважды одновременно
    if (this.#initPromise) return this.#initPromise;

    this.#initPromise = (async () => {
      try {
        const niftiLoader = await import('@cornerstonejs/nifti-volume-loader');
        const init = niftiLoader.init || niftiLoader.default?.init;
        if (init) {
          await init();
          log.debug('[NIfTI] Volume loader successfully registered');
        }
        this.#isRegistered = true;
      } catch (e) {
        throw new NiftiModuleError('Loader dependency unavailable', { error: e });
      } finally {
        this.#initPromise = null;
      }
    })();

    return this.#initPromise;
  });

  loadNiftiFile = this.#withTelemetry('loadNiftiFile', async (
    file, 
    viewportIds, 
    renderingEngineId = 'mikhmelRenderingEngine', 
    onProgress = () => {}
  ) => {
    if (!this.isNiftiFile(file)) {
      throw new NiftiModuleError('Invalid file type. Expected .nii or .nii.gz');
    }

    await this.initLoader();
    onProgress(5, 'Чтение NIfTI файла...');

    // Создаём object URL для передачи в Cornerstone
    const objectUrl = URL.createObjectURL(file);
    const volumeId = `nifti:${objectUrl}`;

    try {
      const re = _getCS().getRenderingEngine(renderingEngineId);
      if (!re) throw new NiftiModuleError(`RenderingEngine "${renderingEngineId}" not found`);

      onProgress(20, 'Создание тома...');
      const volume = await _getCS().volumeLoader.createAndCacheVolume(volumeId, {
        imageIds: [volumeId],
      });

      onProgress(40, 'Назначение вьюпортов...');
      const validVpIds = viewportIds.filter(id => re.getViewport(id));
      if (!validVpIds.length) throw new NiftiModuleError('No valid viewports found for rendering');

      try { rememberVolume(volumeId, [volumeId], validVpIds, {}, 'main'); } catch (e) { /* */ }
      await _getCS().setVolumesForViewports(re, [{ volumeId }], validVpIds);

      onProgress(70, 'Загрузка воксельных данных (RAM)...');
      
      // [FIX #2] Интеллектуальное ожидание загрузки тома вместо setTimeout(1000)
      const loadResult = volume.load();
      if (loadResult instanceof Promise) {
        await loadResult; 
      } else {
        // Fallback: ждем события загрузки или проверяем статус
        await new Promise((resolve) => {
          const checkStatus = () => volume.loadStatus?.loaded ? resolve() : setTimeout(checkStatus, 50);
          checkStatus();
        });
      }

      re.renderViewports(validVpIds);
      onProgress(100, '✅ NIfTI успешно загружен');
      log.debug(`[NIfTI] Loaded: ${file.name} → volumeId: ${volumeId}`);

      return volumeId;
    } finally {
      //[FIX #1] ГАРАНТИРОВАННАЯ ОЧИСТКА ПАМЯТИ.
      // Независимо от того, упала ошибка или том загрузился, NIfTI loader уже считал 
      // данные в ArrayBuffer внутри Cornerstone. Ссылку на файл можно уничтожать.
      URL.revokeObjectURL(objectUrl);
      log.debug('[NIfTI] ObjectURL revoked (Memory freed)');
    }
  });

  // ==========================================
  // 🔍 УТИЛИТЫ И ВАЛИДАЦИЯ
  // ==========================================

  isNiftiFile(file) {
    if (!file || !file.name) return false;
    const name = file.name.toLowerCase();
    return name.endsWith('.nii') || name.endsWith('.nii.gz');
  }

  /** [v8] Public check for loader status */
  isLoaderReady() { return this.#isRegistered; }
  _isRegisteredCheck() { return this.#isRegistered; }

  filterNiftiFiles(files) {
    if (!files || !files.length) return[];
    return Array.from(files).filter(f => this.isNiftiFile(f));
  }

  // ==========================================
  // 💾 ЭКСПОРТ ДАННЫХ (БЕЗ ЗАВИСИМОСТЕЙ)
  // ==========================================

  exportToNifti = this.#withTelemetry('exportToNifti', (
    pixels,
    dims,
    spacing,
    filename = `export_${Date.now()}.nii`,
    метка = null,
    геометрия = null,   // [12.0.54] {origin, direction} тома маски — положение в теле для чужих программ
  ) => {
    // [11.1.70] ВТОРАЯ КОПИЯ ЗАПИСИ БАЙТОВ ПОХОРОНЕНА.
    //
    // Здесь лежало дословное повторение niftiCore.buildNiftiBytes — восемьдесят
    // строк заголовка NIfTI, слово в слово. В самом niftiCore об этом и
    // написано: «перенесено 1:1 из тела exportToNifti… правильный шаг дальше —
    // чтобы niftiModule.exportToNifti собирал буфер отсюда».
    //
    // Шаг сделан, и не для красоты. Я добавлял в заголовок метку «откуда эта
    // маска» — и правка в одну копию оставила бы вторую без метки. Владелец
    // получил бы файлы то с меткой, то без, в зависимости от того, каким путём
    // шёл экспорт, и разбираться в этом пришлось бы ему.
    if (!pixels || !dims || dims.length !== 3 || !spacing || spacing.length !== 3) {
      throw new NiftiModuleError('Invalid input for NIfTI export. Required: pixels, 3D dims, 3D spacing');
    }
    const buffer = buildNiftiBytes(pixels, dims, spacing, метка, геометрия);

    const blob = new Blob([buffer], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);   // некоторые браузеры требуют присутствия в DOM
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 10000);

    const mbSize = (buffer.byteLength / 1024 / 1024).toFixed(1);
    log.info(`[NIfTI] выгружено: ${filename} (${mbSize} МБ)${метка ? `, метка «${метка}»` : ', без метки серии'}`
      + (геометрия ? ', с положением в теле (sform/qform)' : ', БЕЗ положения в теле'));
    return blob;
  });
}

// ==========================================
// 🛡 100% BACKWARDS COMPATIBLE API EXPORTS
// ==========================================
// Сохраняем все твои импорты в других файлах нетронутыми.

const _niftiManager = new NiftiManager();

export function initNiftiLoader() { return _niftiManager.initLoader(); }
export function loadNiftiFile(file, viewportIds, renderingEngineId, onProgress) { 
  return _niftiManager.loadNiftiFile(file, viewportIds, renderingEngineId, onProgress); 
}
export function isNiftiFile(file) { return _niftiManager.isNiftiFile(file); }
export function filterNiftiFiles(files) { return _niftiManager.filterNiftiFiles(files); }
export function exportToNifti(pixels, dims, spacing, filename, метка = null, геометрия = null) {
  return _niftiManager.exportToNifti(pixels, dims, spacing, filename, метка, геометрия);
}

// ── [v8] Enterprise API ──
export function isNiftiLoaderReady() { return _niftiManager.isLoaderReady(); }
export function getNiftiModuleState() {
  return {
    loaderRegistered: _niftiManager._isRegisteredCheck(),
    cornerstoneAvailable: !!_cs,
    supportedExtensions: ['.nii', '.nii.gz'],
  };
}